import { useCallback } from 'react';
import { Button } from '@mantine/core';
import { IconTarget } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useModal } from '@ayocore/platform/client/hooks/useModal';
import LeadPickerModal, {
  type LeadPickerModalProps,
} from '@ayocore/erp/crm/client/components/Leads/LeadPickerModal';
import { useLeadRepository } from '@ayocore/erp/crm/client/rpc';
import type { Lead } from '@ayocore/erp/crm/schemas/lead.schema';

interface LinkLeadActionProps {
  /** Сделка, к которой привязываем заявку. */
  dealId: number;
  /** Колбэк после успешной привязки — перезагрузка карточки. */
  onLinked: () => void;
}

/**
 * Кнопка «Привязать лид» в шапке карточки сделки (Grasias).
 *
 * Связь хранится на заявке (`crm_leads.deal_id`), обратного поля у сделки нет —
 * поэтому привязка это update ЗАЯВКИ, а не сделки.
 *
 * Пикер показывает только непривязанные заявки (`onlyWithoutDeal`): заявка
 * ведёт ровно к одной сделке, и перевешивать её отсюда мы не даём — прятать
 * занятые честнее, чем показать и отказать после выбора.
 *
 * Статус заявки не трогаем. `createDealFromLead` переводит её в «Ушёл в заказ»,
 * но там сделка создаётся из самой заявки, где партнёр уже есть. Здесь же
 * привязать могут сырую заявку без партнёра, а правило закрытия для этого
 * статуса требует и партнёра, и сделку — привязка падала бы. Перевод статуса
 * остаётся осознанным действием менеджера.
 */
export function LinkLeadAction({ dealId, onLinked }: LinkLeadActionProps) {
  const leadRepository = useLeadRepository();
  const picker = useModal<LeadPickerModalProps>(LeadPickerModal);

  const open = useCallback(() => {
    picker.open(
      {
        title: 'Привязать заявку',
        selectionMode: 'single',
        onlyWithoutDeal: true,
        onLeadSelected: async (leads: Lead[]) => {
          picker.close();

          const lead = leads[0];
          if (!lead) return;

          try {
            await leadRepository.update(lead.id, { deal_id: dealId });
            notifications.show({
              color: 'teal',
              message: `Заявка №${lead.id} привязана к сделке`,
            });
            onLinked();
          } catch (error) {
            notifications.show({
              color: 'red',
              title: 'Не удалось привязать заявку',
              message: error instanceof Error ? error.message : 'неизвестная ошибка',
              autoClose: 8000,
            });
          }
        },
        onClose: () => picker.close(),
      },
      { size: 'xl' }
    );
  }, [picker, leadRepository, dealId, onLinked]);

  return (
    <Button variant='light' size='xs' leftSection={<IconTarget size={14} />} onClick={open}>
      Привязать лид
    </Button>
  );
}
